// src/services/learningService.js
import { db, storage } from '../config/firebase';
import { 
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, 
  query, where, orderBy, Timestamp, increment, writeBatch 
} from 'firebase/firestore';

// Course Categories
export const CourseCategory = {
  TECHNOLOGY: 'technology',
  BUSINESS: 'business',
  CREATIVE: 'creative',
  HEALTH: 'health',
  EDUCATION: 'education',
  PERSONAL: 'personal',
  PROFESSIONAL: 'professional',
  LANGUAGE: 'language'
};

// Course Difficulty Levels
export const CourseDifficulty = {
  BEGINNER: 'beginner',
  INTERMEDIATE: 'intermediate',
  ADVANCED: 'advanced',
  EXPERT: 'expert'
};

// Content Block Types
export const BlockType = {
  TEXT: 'text',
  VIDEO: 'video',
  IMAGE: 'image',
  QUIZ: 'quiz',
  ASSESSMENT: 'assessment',
  FILE: 'file',
  EMBED: 'embed'
};

// Assessment Types
export const AssessmentType = {
  MULTIPLE_CHOICE: 'multiple_choice',
  TRUE_FALSE: 'true_false',
  SHORT_ANSWER: 'short_answer',
  ESSAY: 'essay',
  MATCHING: 'matching',
  FILL_BLANK: 'fill_blank'
};

class LearningService {
  // ==================== MARKETPLACE COURSES ====================
  
  async createMarketplaceCourse(bookId, courseData, creatorId) {
    try {
      const courseRef = doc(collection(db, `pb-books/${bookId}/marketplace-courses`));
      const courseId = courseRef.id;

      const newCourse = {
        ...courseData,
        id: courseId,
        creatorId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        enrollmentCount: 0,
        completionCount: 0,
        rating: 0,
        ratingCount: 0,
        isPublished: false,
        featured: false
      };

      await setDoc(courseRef, newCourse);
      return courseId;
    } catch (error) {
      console.error('Error creating marketplace course:', error);
      throw error;
    }
  }

  async updateMarketplaceCourse(bookId, courseId, updates) {
    try {
      const courseRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}`);
      await updateDoc(courseRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating marketplace course:', error);
      throw error;
    }
  }

  async deleteMarketplaceCourse(bookId, courseId) {
    try {
      const courseRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}`);
      await deleteDoc(courseRef);
    } catch (error) {
      console.error('Error deleting marketplace course:', error);
      throw error;
    }
  }

  async getMarketplaceCourse(bookId, courseId) {
    try {
      const courseRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}`);
      const courseDoc = await getDoc(courseRef);
      return courseDoc.exists() ? { id: courseDoc.id, ...courseDoc.data() } : null;
    } catch (error) {
      console.error('Error getting marketplace course:', error);
      throw error;
    }
  }

  async getMarketplaceCourses(bookId, filters = {}) {
    try {
      let coursesQuery = collection(db, `pb-books/${bookId}/marketplace-courses`);
      const constraints = [];

      if (filters.category) {
        constraints.push(where('category', '==', filters.category));
      }
      if (filters.difficulty) {
        constraints.push(where('difficulty', '==', filters.difficulty));
      }
      if (filters.isFree !== undefined) {
        constraints.push(where('isFree', '==', filters.isFree));
      }
      if (filters.isPublished !== undefined) {
        constraints.push(where('isPublished', '==', filters.isPublished));
      }
      if (filters.featured) {
        constraints.push(where('featured', '==', true));
      }

      constraints.push(orderBy('createdAt', 'desc'));

      if (constraints.length > 0) {
        coursesQuery = query(coursesQuery, ...constraints);
      }

      const snapshot = await getDocs(coursesQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting marketplace courses:', error);
      throw error;
    }
  }

  async searchMarketplaceCourses(bookId, searchTerm) {
    try {
      const allCourses = await this.getMarketplaceCourses(bookId, { isPublished: true });
      const term = searchTerm.toLowerCase();
      
      return allCourses.filter(course => 
        course.title?.toLowerCase().includes(term) ||
        course.description?.toLowerCase().includes(term) ||
        course.tags?.some(tag => tag.toLowerCase().includes(term)) ||
        course.instructor?.toLowerCase().includes(term)
      );
    } catch (error) {
      console.error('Error searching marketplace courses:', error);
      throw error;
    }
  }

  // ==================== COURSE CONTENT ====================

  async createLesson(bookId, courseId, lessonData) {
    try {
      const lessonRef = doc(collection(db, `pb-books/${bookId}/marketplace-courses/${courseId}/lessons`));
      const lessonId = lessonRef.id;

      const newLesson = {
        ...lessonData,
        id: lessonId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      await setDoc(lessonRef, newLesson);
      
      // Update course lesson count
      await this.updateCourseStats(bookId, courseId);
      
      return lessonId;
    } catch (error) {
      console.error('Error creating lesson:', error);
      throw error;
    }
  }

  async updateLesson(bookId, courseId, lessonId, updates) {
    try {
      const lessonRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/lessons/${lessonId}`);
      await updateDoc(lessonRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating lesson:', error);
      throw error;
    }
  }

  async deleteLesson(bookId, courseId, lessonId) {
    try {
      const lessonRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/lessons/${lessonId}`);
      await deleteDoc(lessonRef);
      
      // Update course stats
      await this.updateCourseStats(bookId, courseId);
    } catch (error) {
      console.error('Error deleting lesson:', error);
      throw error;
    }
  }

  async getLesson(bookId, courseId, lessonId) {
    try {
      const lessonRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/lessons/${lessonId}`);
      const lessonDoc = await getDoc(lessonRef);
      return lessonDoc.exists() ? { id: lessonDoc.id, ...lessonDoc.data() } : null;
    } catch (error) {
      console.error('Error getting lesson:', error);
      throw error;
    }
  }

  async getLessons(bookId, courseId) {
    try {
      const lessonsQuery = query(
        collection(db, `pb-books/${bookId}/marketplace-courses/${courseId}/lessons`),
        orderBy('order', 'asc')
      );
      const snapshot = await getDocs(lessonsQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting lessons:', error);
      throw error;
    }
  }

  async reorderLessons(bookId, courseId, lessonIds) {
    try {
      const batch = writeBatch(db);
      lessonIds.forEach((lessonId, index) => {
        const lessonRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/lessons/${lessonId}`);
        batch.update(lessonRef, { order: index });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error reordering lessons:', error);
      throw error;
    }
  }

  // ==================== ENROLLMENTS ====================

  async enrollUser(bookId, courseId, userId, paymentInfo = null) {
    try {
      const enrollmentRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/enrollments/${userId}`);
      
      await setDoc(enrollmentRef, {
        userId,
        courseId,
        enrolledAt: Timestamp.now(),
        lastAccessedAt: Timestamp.now(),
        progress: 0,
        completedLessons: [],
        completedAssessments: {},
        competencyScores: {},
        totalTimeSpent: 0,
        paymentInfo: paymentInfo || null,
        status: 'active'
      });

      // Update course enrollment count
      const courseRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}`);
      await updateDoc(courseRef, {
        enrollmentCount: increment(1)
      });

      return enrollmentRef.id;
    } catch (error) {
      console.error('Error enrolling user:', error);
      throw error;
    }
  }

  async getEnrollment(bookId, courseId, userId) {
    try {
      const enrollmentRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/enrollments/${userId}`);
      const enrollmentDoc = await getDoc(enrollmentRef);
      return enrollmentDoc.exists() ? { id: enrollmentDoc.id, ...enrollmentDoc.data() } : null;
    } catch (error) {
      console.error('Error getting enrollment:', error);
      throw error;
    }
  }

  async getUserEnrollments(bookId, userId) {
    try {
      const coursesRef = collection(db, `pb-books/${bookId}/marketplace-courses`);
      const coursesSnapshot = await getDocs(coursesRef);
      
      const enrollments = [];
      for (const courseDoc of coursesSnapshot.docs) {
        const enrollmentRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseDoc.id}/enrollments/${userId}`);
        const enrollmentDoc = await getDoc(enrollmentRef);
        
        if (enrollmentDoc.exists()) {
          enrollments.push({
            courseId: courseDoc.id,
            course: { id: courseDoc.id, ...courseDoc.data() },
            ...enrollmentDoc.data()
          });
        }
      }
      
      return enrollments;
    } catch (error) {
      console.error('Error getting user enrollments:', error);
      throw error;
    }
  }

  async unenrollUser(bookId, courseId, userId) {
    try {
      const enrollmentRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/enrollments/${userId}`);
      await deleteDoc(enrollmentRef);

      // Update course enrollment count
      const courseRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}`);
      await updateDoc(courseRef, {
        enrollmentCount: increment(-1)
      });
    } catch (error) {
      console.error('Error unenrolling user:', error);
      throw error;
    }
  }

  // ==================== PROGRESS TRACKING ====================

  async updateProgress(bookId, courseId, userId, lessonId, completedBlocks = []) {
    try {
      const enrollmentRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/enrollments/${userId}`);
      const enrollmentDoc = await getDoc(enrollmentRef);
      
      if (!enrollmentDoc.exists()) {
        throw new Error('Enrollment not found');
      }

      const enrollment = enrollmentDoc.data();
      const completedLessons = enrollment.completedLessons || [];
      
      if (!completedLessons.includes(lessonId)) {
        completedLessons.push(lessonId);
      }

      // Calculate progress
      const lessons = await this.getLessons(bookId, courseId);
      const progress = (completedLessons.length / lessons.length) * 100;

      await updateDoc(enrollmentRef, {
        completedLessons,
        progress,
        lastAccessedAt: Timestamp.now()
      });

      return { progress, completedLessons };
    } catch (error) {
      console.error('Error updating progress:', error);
      throw error;
    }
  }

  async recordAssessmentResult(bookId, courseId, userId, lessonId, assessmentId, result) {
    try {
      const enrollmentRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/enrollments/${userId}`);
      const enrollmentDoc = await getDoc(enrollmentRef);
      
      if (!enrollmentDoc.exists()) {
        throw new Error('Enrollment not found');
      }

      const enrollment = enrollmentDoc.data();
      const completedAssessments = enrollment.completedAssessments || {};
      
      completedAssessments[`${lessonId}_${assessmentId}`] = {
        ...result,
        completedAt: Timestamp.now()
      };

      await updateDoc(enrollmentRef, {
        completedAssessments
      });

      // Update competency scores
      await this.updateCompetencyScores(bookId, courseId, userId);
    } catch (error) {
      console.error('Error recording assessment result:', error);
      throw error;
    }
  }

  async updateCompetencyScores(bookId, courseId, userId) {
    try {
      const enrollmentRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/enrollments/${userId}`);
      const enrollmentDoc = await getDoc(enrollmentRef);
      
      if (!enrollmentDoc.exists()) return;

      const enrollment = enrollmentDoc.data();
      const completedAssessments = enrollment.completedAssessments || {};
      
      // Calculate competency scores by topic/skill
      const competencyScores = {};
      const course = await this.getMarketplaceCourse(bookId, courseId);
      
      if (course?.competencies) {
        course.competencies.forEach(competency => {
          const relatedAssessments = Object.entries(completedAssessments).filter(
            ([key, result]) => result.competencyId === competency.id
          );
          
          if (relatedAssessments.length > 0) {
            const avgScore = relatedAssessments.reduce((sum, [, result]) => 
              sum + (result.score || 0), 0) / relatedAssessments.length;
            
            competencyScores[competency.id] = {
              name: competency.name,
              score: avgScore,
              assessmentCount: relatedAssessments.length
            };
          }
        });
      }

      await updateDoc(enrollmentRef, {
        competencyScores
      });
    } catch (error) {
      console.error('Error updating competency scores:', error);
      throw error;
    }
  }

  async generateCompetencyReport(bookId, courseId, userId) {
    try {
      const enrollment = await this.getEnrollment(bookId, courseId, userId);
      const course = await this.getMarketplaceCourse(bookId, courseId);
      const lessons = await this.getLessons(bookId, courseId);
      
      if (!enrollment) {
        throw new Error('Enrollment not found');
      }

      const report = {
        userId,
        courseId,
        courseName: course?.title || '',
        generatedAt: Timestamp.now(),
        overallProgress: enrollment.progress || 0,
        completedLessons: enrollment.completedLessons?.length || 0,
        totalLessons: lessons.length,
        competencyScores: enrollment.competencyScores || {},
        assessmentResults: enrollment.completedAssessments || {},
        totalTimeSpent: enrollment.totalTimeSpent || 0,
        recommendations: this.generateRecommendations(enrollment, course)
      };

      // Save report
      const reportRef = doc(collection(db, `pb-books/${bookId}/competency-reports`));
      await setDoc(reportRef, report);

      return report;
    } catch (error) {
      console.error('Error generating competency report:', error);
      throw error;
    }
  }

  generateRecommendations(enrollment, course) {
    const recommendations = [];
    const competencyScores = enrollment.competencyScores || {};
    
    // Identify weak areas
    Object.entries(competencyScores).forEach(([id, data]) => {
      if (data.score < 70) {
        recommendations.push({
          type: 'improvement',
          competency: data.name,
          message: `Consider reviewing ${data.name} content to strengthen understanding.`,
          priority: 'high'
        });
      }
    });

    // Check incomplete lessons
    const totalLessons = course?.lessonCount || 0;
    const completedLessons = enrollment.completedLessons?.length || 0;
    
    if (completedLessons < totalLessons) {
      recommendations.push({
        type: 'progress',
        message: `Complete remaining ${totalLessons - completedLessons} lessons to finish the course.`,
        priority: 'medium'
      });
    }

    return recommendations;
  }

  // ==================== HELPER METHODS ====================

  async updateCourseStats(bookId, courseId) {
    try {
      const lessons = await this.getLessons(bookId, courseId);
      const totalDuration = lessons.reduce((sum, lesson) => sum + (lesson.duration || 0), 0);
      
      const courseRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}`);
      await updateDoc(courseRef, {
        lessonCount: lessons.length,
        estimatedDuration: totalDuration,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating course stats:', error);
      throw error;
    }
  }

  async recordTimeSpent(bookId, courseId, userId, seconds) {
    try {
      const enrollmentRef = doc(db, `pb-books/${bookId}/marketplace-courses/${courseId}/enrollments/${userId}`);
      await updateDoc(enrollmentRef, {
        totalTimeSpent: increment(seconds),
        lastAccessedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error recording time spent:', error);
      throw error;
    }
  }
}

export const learningService = new LearningService();